// Schematische Skizze eines Flurs aus Abschnitten (14.10.2026): gerade, mit Ecken, mit Abzweigen. Kein Maßstab.
// Abschnitt 1 beginnt unten am Eingang und führt nach oben; an einer Ecke knickt der Gang nach links oder rechts
// ab (in Gehrichtung), ein Abzweig geht seitlich von seinem Abschnitt ab. Räume stehen als Kästen links und rechts
// in der Reihenfolge ihrer Türen, Räume am Ende eines Abschnitts davor. Reine Funktion: dieselbe Skizze in der
// Erfassung (Grundriss-Schritt) und im Bericht. Farben als Attribute, damit sie ohne Stylesheet stimmen.
// Seit 30.10.2026 mit der gezogenen Lage: Türen, Abzweige („bei“) und seitliche Wohnungstür in ihrer Reihenfolge an
// der Wand, Türen an der Eingangswand unter dem Anfang des Flurs.

import type { HallSection, HallSide, RoomWall } from "./konstanten.ts";

/** Raum hinter einem Zimmer (15.10.2026), z. B. Balkon hinter dem Wohnzimmer: wall = Wand des Zimmers mit der Tür. */
export type SketchChild = { id?: string; label: string; wall: RoomWall | null; children?: SketchChild[] };

export type SketchDoor = {
  /** Text im Raumkasten, z. B. „Bad“; leer = noch kein Raum zugeordnet. */
  label: string;
  section: number;
  side: HallSide | null;
  order: number;
  /** Nummer an der Tür (wie in der Türliste der Erfassung), optional. */
  nr?: string;
  /** Für Verbindungstüren zwischen Zimmern (opts.links). */
  id?: string;
  /** Räume hinter diesem Zimmer (15.10.2026): außen am Zimmer, Türmarke an der Wand. */
  children?: SketchChild[];
  /** Lage an der Wand 0..1 (rooms.hall_pos, 30.10.2026): ordnet Türen, Abzweige und seitlichen Eingang richtig ein. */
  pos?: number | null;
};

/** Platz an einer Seite des Gangs: Tür, Abzweig oder (Abschnitt 1) die seitliche Wohnungstür. */
type Slot = { kind: "tuer"; door: SketchDoor; p: number } | { kind: "abzweig"; br: HallSection; p: number } | { kind: "eingang"; p: number };

type Pt = { x: number; y: number };
/** Richtungen: 0 = hoch, 1 = rechts, 2 = runter, 3 = links. */
const DIRS: Pt[] = [
  { x: 0, y: -1 },
  { x: 1, y: 0 },
  { x: 0, y: 1 },
  { x: -1, y: 0 },
];
const HALL = 34;
const SLOT = 86;
const DEPTH = 80;
const GAP = 6;
/** Vorlauf am Anfang eines Abschnitts nach einer Ecke oder einem Abzweig, damit Raumkästen nicht ineinander laufen. */
const LEAD = HALL / 2 + GAP + DEPTH;

const add = (a: Pt, b: Pt, k = 1): Pt => ({ x: a.x + b.x * k, y: a.y + b.y * k });
const escXml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Abschnitte vervollständigen: Abschnitt 1 gibt es immer; jeder andere hängt an einem Gang (`von`). Ältere Aufnahmen
 * (bis 30.10.2026) hatten Ecken ohne `von` als Kette: die Ecke hängt am vorigen Abschnitt des Hauptwegs, ein Abzweig an 1.
 */
export function normalizeSections(sections: HallSection[] | null | undefined): HallSection[] {
  const list = [...(sections ?? [])].filter((s) => s.nr >= 1).sort((a, b) => a.nr - b.nr);
  if (!list.some((s) => s.nr === 1)) list.unshift({ nr: 1, art: "start", richtung: null, von: null });
  const nrs = new Set(list.map((s) => s.nr));
  let mainPrev = 1;
  return list.map((s) => {
    if (s.nr === 1) return { ...s, art: "start", richtung: null, von: null };
    const known = s.von != null && s.von !== s.nr && nrs.has(s.von);
    const von = known ? s.von! : s.art === "ecke" ? mainPrev : 1;
    if (s.art === "ecke" && !known) mainPrev = s.nr;
    return { ...s, von };
  });
}

/** Gänge am Ende eines Gangs: Ecken (zwei = T) und bei der Diele „geradeaus“. */
export const endChildren = (sections: HallSection[], nr: number) =>
  sections.filter((x) => x.von === nr && x.nr !== nr && (x.art === "ecke" || x.richtung === "geradeaus"));
/** Abzweige unterwegs (an der Stelle `bei`), seitlich links oder rechts. */
export const branchChildren = (sections: HallSection[], nr: number) =>
  sections.filter((x) => x.von === nr && x.nr !== nr && x.art === "abzweig" && x.richtung !== "geradeaus");

/** Reihenfolge zum Ablaufen: Gang, dann um seine Ecken weiter, dann seine Abzweige nach ihrer Stelle (auch verschachtelt). */
export function sectionOrder(sectionsIn: HallSection[] | null | undefined): HallSection[] {
  const sections = normalizeSections(sectionsIn);
  const out: HallSection[] = [];
  const seen = new Set<number>();
  const visit = (s: HallSection) => {
    if (seen.has(s.nr)) return;
    seen.add(s.nr);
    out.push(s);
    for (const c of endChildren(sections, s.nr).sort((a, b) => a.nr - b.nr)) visit(c);
    for (const c of branchChildren(sections, s.nr).sort((a, b) => (a.bei ?? 0.5) - (b.bei ?? 0.5) || a.nr - b.nr)) visit(c);
  };
  visit(sections[0]!);
  for (const s of sections) visit(s);
  return out;
}

/** Zeilen für den Kasten: höchstens zwei, am Leerzeichen umbrochen, sonst gekürzt. */
function lines(text: string, max: number): string[] {
  if (text.length <= max) return [text];
  const cut = text.lastIndexOf(" ", max);
  if (cut > 2) {
    const rest = text.slice(cut + 1);
    return [text.slice(0, cut), rest.length > max ? `${rest.slice(0, max - 1)}…` : rest];
  }
  return [`${text.slice(0, max - 1)}…`];
}

export function hallSketchSvg(
  sectionsIn: HallSection[] | null | undefined,
  doors: SketchDoor[],
  opts: {
    title?: string;
    /** Verbindungstüren zwischen zwei Zimmern (Ids), gestrichelt. */ links?: [string, string][];
    /** Räume ohne Flurtür (Keller, Garage …): als Blasen unter dem Eingang (30.10.2026). */ extras?: SketchChild[];
    /** Beschriftung des Startpunkts (Eingang bzw. Treppe). */ start?: string;
    /** Etage ohne Flur: nur die Räume als Blasen. */ ohneGang?: boolean;
  } = {},
): string {
  const sections = normalizeSections(sectionsIn);
  const maxNr = Math.max(...sections.map((s) => s.nr));
  const sec = (d: SketchDoor) => Math.min(Math.max(1, d.section || 1), maxNr);
  const shapes: string[] = [];
  const pts: Pt[] = [];
  const box = (c: Pt, w: number, h: number, label: string, id?: string) => {
    pts.push({ x: c.x - w / 2, y: c.y - h / 2 }, { x: c.x + w / 2, y: c.y + h / 2 });
    const ls = label ? lines(label, Math.floor(w / 6)) : ["?"];
    const ty = c.y - ((ls.length - 1) * 11) / 2 + 3.5;
    shapes.push(
      (id ? `<g data-room="${escXml(id)}" class="raum-blase">` : "") +
        `<rect x="${c.x - w / 2}" y="${c.y - h / 2}" width="${w}" height="${h}" rx="14" fill="#fff" stroke="${label ? "#7c9a9e" : "#e2a03f"}" stroke-width="1.2"${label ? "" : ' stroke-dasharray="4 3"'}/>` +
        ls.map((l, i) => `<text x="${c.x}" y="${ty + i * 11}" text-anchor="middle" font-size="10" fill="#142229">${escXml(l)}</text>`).join("") +
        (id ? "</g>" : ""),
    );
  };
  const marker = (c: Pt, nr: string | undefined) => {
    if (!nr) return;
    shapes.push(`<circle cx="${c.x}" cy="${c.y}" r="8" fill="#e2a03f" stroke="#fff" stroke-width="1.5"/><text x="${c.x}" y="${c.y + 3.5}" text-anchor="middle" font-size="9.5" font-weight="700" fill="#142229">${escXml(nr)}</text>`);
  };
  /** Pfeil in den Flur hinein an der Wandstelle m (n zeigt nach außen), daneben „Eingang“ bzw. „Treppe“. */
  const entranceArrow = (m: Pt, n: Pt) => {
    const tip = add(m, n, 2);
    const tail = add(m, n, 22);
    const t = { x: -n.y, y: n.x };
    const h1 = add(add(tip, n, 7), t, 5);
    const h2 = add(add(tip, n, 7), t, -5);
    shapes.push(`<path d="M${tail.x} ${tail.y} L${tip.x} ${tip.y} M${h1.x} ${h1.y} L${tip.x} ${tip.y} L${h2.x} ${h2.y}" stroke="#0e5a63" stroke-width="2" fill="none"/>`);
    const lp = add(m, n, n.x === 0 ? 34 : 28);
    const anchor = n.x < 0 ? "end" : n.x > 0 ? "start" : "middle";
    shapes.push(`<text x="${lp.x}" y="${lp.y + 3.5}" text-anchor="${anchor}" font-size="10" fill="#5b686c">${escXml(opts.start ?? "Eingang")}</text>`);
    pts.push(add(m, n, n.x === 0 ? 40 : 80));
  };
  const corridors: string[] = [];
  const centers = new Map<string, Pt>();
  /**
   * Räume hinter einem Zimmer: außen (in Blickrichtung n vom Flur ins Zimmer) nebeneinander, Türmarke an der Wand des
   * Zimmers (gegenüber = n, rechts/links quer dazu, Türwand = zum Flur). Verschachtelt weiter außen.
   */
  const attach = (id: string | undefined, c: Pt, w: number, h: number, n: number, children: SketchChild[] | undefined) => {
    if (id) centers.set(id, c);
    if (!children?.length) return;
    const nv = DIRS[n]!;
    const across = DIRS[(n + 1) % 4]!;
    const halfN = nv.x === 0 ? h / 2 : w / 2;
    children.forEach((ch, i) => {
      const off = (i - (children.length - 1) / 2) * SLOT;
      const cc = add(add(c, nv, halfN + GAP + DEPTH / 2), across, off);
      const cw = nv.x === 0 ? SLOT - GAP : DEPTH;
      const chh = nv.x === 0 ? DEPTH : SLOT - GAP;
      box(cc, cw, chh, ch.label, ch.id);
      // Türmarke an der Wand des Zimmers.
      const wd = ch.wall === "rechts" ? (n + 1) % 4 : ch.wall === "links" ? (n + 3) % 4 : ch.wall === "tuer" ? (n + 2) % 4 : n;
      const wv = DIRS[wd]!;
      const half = wv.x === 0 ? h / 2 : w / 2;
      const m = add(c, wv, half);
      const t = DIRS[(wd + 1) % 4]!;
      shapes.push(`<line x1="${add(m, t, -10).x}" y1="${add(m, t, -10).y}" x2="${add(m, t, 10).x}" y2="${add(m, t, 10).y}" stroke="#e2a03f" stroke-width="4"/>`);
      // Seitliche Tür: dünne Verbindung zum Raum dahinter.
      if (wd !== n) links.push(`<path d="M${m.x} ${m.y} L${cc.x} ${cc.y}" stroke="#7c9a9e" stroke-width="1.2" stroke-dasharray="3 3" fill="none"/>`);
      attach(ch.id, cc, cw, chh, n, ch.children);
    });
  };
  const links: string[] = [];

  // Wohnungstür (30.10.2026): unten an der Stirnseite (Standard) oder seitlich an Abschnitt 1.
  const eingang = sections[0]!.eingang ?? { seite: "anfang" as const, pos: 0.5 };
  /**
   * Plätze einer Seite in Gehrichtung. Mit gespeicherter Lage (hall_pos, Abzweig „bei“, seitlicher Eingang) nach der
   * Lage gemischt; ältere Aufnahmen ohne Lage wie bisher: Türen nach Reihenfolge, Abzweige dahinter.
   */
  const slotsOf = (s: HallSection, list: SketchDoor[], branches: HallSection[], entrance: number | null): Slot[] => {
    const withPos = list.every((x) => x.pos != null);
    return [
      ...list.map((door, i): Slot => ({ kind: "tuer", door, p: withPos ? door.pos! : i / 1000 })),
      ...branches.map((br, k): Slot => ({ kind: "abzweig", br, p: withPos ? (s.kurz ? 1 : (br.bei ?? 0.5)) : 1 + k })),
      ...(entrance != null ? [{ kind: "eingang", p: entrance } as Slot] : []),
    ].sort((a, b) => a.p - b.p);
  };

  const placed = new Set<number>();
  const place = (s: HallSection, start: Pt, dir: number) => {
    if (placed.has(s.nr)) return;
    placed.add(s.nr);
    const d = DIRS[dir]!;
    const own = doors.filter((x) => sec(x) === s.nr).sort((a, b) => a.order - b.order);
    const left = own.filter((x) => x.side === "links");
    const right = own.filter((x) => x.side === "rechts");
    const end = own.filter((x) => x.side === "ende" || x.side == null);
    const branchesL = branchChildren(sections, s.nr).filter((b) => b.richtung === "links");
    const branchesR = branchChildren(sections, s.nr).filter((b) => b.richtung !== "links");
    const seitlich = s.art === "start" && eingang.seite !== "anfang" ? eingang : null;
    const slotsL = slotsOf(s, left, branchesL, seitlich?.seite === "links" ? seitlich.pos : null);
    const slotsR = slotsOf(s, right, branchesR, seitlich?.seite === "rechts" ? seitlich.pos : null);
    const lead = s.art === "start" ? 0 : LEAD;
    const slots = Math.max(slotsL.length, slotsR.length, 1);
    const len = lead + GAP * 2 + slots * SLOT;
    const e = add(start, d, len);
    // Gang: über die Endpunkte hinaus um die halbe Breite, damit Ecken geschlossen sind (außer am Eingang).
    const a = add(start, d, s.art === "start" ? 0 : -HALL / 2);
    const b = add(e, d, HALL / 2);
    const x0 = Math.min(a.x, b.x) - (d.x === 0 ? HALL / 2 : 0);
    const y0 = Math.min(a.y, b.y) - (d.y === 0 ? HALL / 2 : 0);
    const w = d.x === 0 ? HALL : Math.abs(b.x - a.x);
    const h = d.y === 0 ? HALL : Math.abs(b.y - a.y);
    corridors.push(`<rect x="${x0}" y="${y0}" width="${w}" height="${h}" fill="#e8f1f2" stroke="#0e5a63" stroke-width="1.5"/>`);
    pts.push({ x: x0, y: y0 }, { x: x0 + w, y: y0 + h });
    // Nummer des Abschnitts am Anfang des Gangs (nur bei mehreren Abschnitten), nicht zwischen den Türnummern.
    if (sections.length > 1) {
      const m = add(start, d, lead + GAP + 6);
      shapes.push(`<text x="${m.x}" y="${m.y + 4}" text-anchor="middle" font-size="11" font-weight="700" fill="#0e5a63">${s.nr}</text>`);
    }
    const sideBoxes = (list: Slot[], nDir: number) => {
      const n = DIRS[nDir]!;
      const bw = d.x === 0 ? DEPTH : SLOT - GAP;
      const bh = d.x === 0 ? SLOT - GAP : DEPTH;
      list.forEach((slot, i) => {
        const along = add(start, d, lead + GAP + (i + 0.5) * SLOT);
        if (slot.kind === "abzweig") return place(slot.br, add(along, n, HALL / 2), nDir);
        if (slot.kind === "eingang") return entranceArrow(add(along, n, HALL / 2), n);
        const door = slot.door;
        const c = add(along, n, HALL / 2 + GAP + DEPTH / 2);
        box(c, bw, bh, door.label, door.id);
        attach(door.id, c, bw, bh, nDir, door.children);
        shapes.push(`<line x1="${add(add(along, d, -14), n, HALL / 2).x}" y1="${add(add(along, d, -14), n, HALL / 2).y}" x2="${add(add(along, d, 14), n, HALL / 2).x}" y2="${add(add(along, d, 14), n, HALL / 2).y}" stroke="#e2a03f" stroke-width="4"/>`);
        marker(add(along, n, HALL / 2), door.nr);
      });
    };
    sideBoxes(slotsL, (dir + 3) % 4);
    sideBoxes(slotsR, (dir + 1) % 4);
    // Räume am Ende des Abschnitts, quer nebeneinander.
    end.forEach((door, i) => {
      const n = DIRS[(dir + 1) % 4]!;
      const off = (i - (end.length - 1) / 2) * SLOT;
      const c = add(add(e, d, HALL / 2 + GAP + DEPTH / 2), n, off);
      box(c, d.x === 0 ? SLOT - GAP : DEPTH, d.x === 0 ? DEPTH : SLOT - GAP, door.label, door.id);
      attach(door.id, c, d.x === 0 ? SLOT - GAP : DEPTH, d.x === 0 ? DEPTH : SLOT - GAP, dir, door.children);
      marker(add(add(e, d, HALL / 2), n, off), door.nr);
    });
    // Weiter um die Ecke(n) am Ende dieses Gangs (zwei Ecken = T), bei der Diele auch geradeaus.
    for (const next of endChildren(sections, s.nr)) {
      place(next, e, next.richtung === "links" ? (dir + 3) % 4 : next.richtung === "rechts" ? (dir + 1) % 4 : dir);
    }
  };
  place(sections[0]!, { x: 0, y: 0 }, 0);
  // Verbindungstüren zwischen zwei Zimmern: gestrichelt von Mitte zu Mitte (die Kästen decken die Mitte ab).
  for (const [a, b] of opts.links ?? []) {
    const pa = centers.get(a), pb = centers.get(b);
    if (pa && pb) links.push(`<path d="M${pa.x} ${pa.y} L${pb.x} ${pb.y}" stroke="#e2a03f" stroke-width="2" stroke-dasharray="5 4" fill="none"/>`);
  }
  // Eingang unter Abschnitt 1 (seitlicher Eingang steht schon an seiner Seite).
  if (eingang.seite === "anfang") entranceArrow({ x: (Math.min(1, Math.max(0, eingang.pos)) - 0.5) * HALL * 0.8, y: 0 }, { x: 0, y: 1 });
  pts.push({ x: -30, y: 40 }, { x: 30, y: 0 });
  // Türen an der Eingangswand (30.10.2026): unter dem Anfang des Flurs, links und rechts neben der Wohnungstür.
  const anfang = doors.filter((x) => x.side === "anfang").sort((a, b) => (a.pos ?? 0.5) - (b.pos ?? 0.5));
  const unten = (door: SketchDoor, x: number) => {
    const c = { x, y: GAP + DEPTH / 2 };
    box(c, SLOT - GAP, DEPTH, door.label, door.id);
    attach(door.id, c, SLOT - GAP, DEPTH, 2, door.children);
    shapes.push(`<line x1="${x - 14}" y1="${GAP}" x2="${x + 14}" y2="${GAP}" stroke="#e2a03f" stroke-width="4"/>`);
    marker({ x, y: GAP }, door.nr);
  };
  if (eingang.seite === "anfang") {
    const vor = anfang.filter((x) => (x.pos ?? 0.5) < eingang.pos).reverse();
    const nach = anfang.filter((x) => (x.pos ?? 0.5) >= eingang.pos);
    vor.forEach((door, k) => unten(door, -(SLOT / 2 + 30 + k * SLOT)));
    nach.forEach((door, k) => unten(door, SLOT / 2 + 30 + k * SLOT));
  } else anfang.forEach((door, i) => unten(door, (i - (anfang.length - 1) / 2) * SLOT));
  // Räume ohne Flurtür: Blasen in Reihen zu dreien unter dem Eingang.
  const extrasTop = anfang.length ? GAP + DEPTH + 24 : 60;
  (opts.extras ?? []).forEach((r, i) => {
    const row = Math.floor(i / 3);
    const inRow = Math.min(3, (opts.extras ?? []).length - row * 3);
    const c = { x: ((i % 3) - (inRow - 1) / 2) * SLOT, y: extrasTop + DEPTH / 2 + row * (DEPTH + GAP) };
    box(c, SLOT - GAP, DEPTH - 20, r.label, r.id);
  });

  const minX = Math.min(...pts.map((p) => p.x)) - 8;
  const minY = Math.min(...pts.map((p) => p.y)) - 8;
  const W = Math.ceil(Math.max(...pts.map((p) => p.x)) - minX + 8);
  const H = Math.ceil(Math.max(...pts.map((p) => p.y)) - minY + 8);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" class="flurskizze" viewBox="${minX} ${minY} ${W} ${H}" width="${W}" height="${H}" role="img" aria-label="${escXml(opts.title ?? "Skizze des Flurs")}" font-family="system-ui, sans-serif">` +
    (opts.ohneGang ? "" : corridors.join("")) +
    links.join("") +
    shapes.join("") +
    "</svg>"
  );
}
